import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { SortingState } from "@tanstack/react-table";
import { useOpenOnNew } from "@/hooks/useOpenOnNew";
import {
  Add,
  ChevronDown,
  Export,
  Import,
  Loader,
  Sort
} from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import DropdownMenuListItem from "@/components/controls/OptionsDropdownMenu/DropdownMenuListItem";
import { Divider } from "@/components/ui/divider";
import { LeadColumn, LeadData } from "@/pages/leads/types";
import { leadGroupAggregationField } from "@/pages/leads/groupSummaries";
import { useLeadsInfinite, useResetLeadsInfinite } from "./hooks";
import { isEqual } from "lodash";
import { ComputedFields, EmptyFilters } from "@/services/api/types";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import CreateLeadModal from "@/pages/leads/components/CreateLeadModal";
import { UserData } from "@/entities/users/types";
import { leadsColumnKeys } from "@/pages/leads/helpers";
import { ColumnData } from "@/entities/dataTables/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId,
  excludeFieldsByKey,
  excludePartnerFields,
  mergeMissingDefaultColumns,
  withColumnsVisible,
  LEAD_ICP_FIT_FIELD_KEYS
} from "@/entities/dataTables/helpers";
import { useReconciledColumnsState } from "@/entities/dataTables/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import LeadsBottomKPIBar from "@/components/controls/BottomKPIBar/LeadsBottomKPIBar";
import { useFieldVisibility, useEntityFields } from "@/pages/settings/hooks";
import { useAccountSettings } from "@/entities/settings/hooks";
import { Button } from "@/components/ui/button";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import ImportLeadsModal from "./components/ImportLeadsModal/components/ImportLeadsModal";
import {
  ExportFormat,
  useEntityExport
} from "@/services/hooks/entities/useEntityExport";
import LeadsTable from "@/pages/leads/components/LeadsTable/LeadsTable";
import {
  useLeadsFilterOptions,
  useLeadsGroupByOptions
} from "@/pages/leads/filters";

interface Props {
  userData: UserData;
}

const TABLE_ID = "leads-table";
// Module-level so the reconcile effect's dependency stays referentially stable.
const NO_FLAG_GATED_COLUMN_IDS: readonly string[] = [];

const Leads: React.FC<Props> = ({ userData }) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EmptyFilters);
  const [sorting, onSortingChange] = useState<SortingState>([
    { id: LeadColumn.NAME, desc: false }
  ]);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);

  // The import/export menu is controlled so selecting "Export" can keep it open
  // while the CSV is generated; it closes on outside-click or once export ends.
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const keepMenuOpenForExportRef = useRef(false);
  const wasExportingRef = useRef(false);

  const handleActionsMenuOpenChange = useCallback((open: boolean) => {
    if (!open && keepMenuOpenForExportRef.current) {
      // Suppress all close attempts (Radix auto-close + outside-click) while
      // export is in progress so the loader stays visible until it completes.
      return;
    }
    setIsActionsMenuOpen(open);
  }, []);

  const [viewReady, setViewReady] = useState(false);
  const isActivatingViewRef = useRef(false);
  const handleViewActivated = useCallback(() => {
    isActivatingViewRef.current = true;
    setViewReady(true);
    // Clear guard after React processes batched state updates,
    // in case filters didn't change and the [filters] effect doesn't fire
    setTimeout(() => {
      isActivatingViewRef.current = false;
    }, 0);
  }, []);
  const sortParams = useMemo(() => {
    const fieldId = sorting[0].id ?? LeadColumn.NAME;
    let field = fieldId;
    if (fieldId === LeadColumn.CONTACT && EntityType.LEAD) {
      field = "contactFirstName";
    }
    return {
      field,
      asc: !sorting[0].desc
    };
  }, [sorting]);
  const leadsSortByOptions = [
    LeadColumn.NAME,
    LeadColumn.COMPANY_NAME,
    LeadColumn.STATUS,
    LeadColumn.OPEN_FOR_DAYS
  ];
  const [groupBy, setGroupBy] = useState<string[]>([]);
  // Grouping the aggregations by the same field the table groups on costs no
  // extra request — they are one query — and returns every group's figures up
  // front instead of whatever infinite scroll has reached.
  const aggregationsGroupField = (leadGroupAggregationField(groupBy[0]) ??
    "all") as keyof LeadData;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [candidateData, clearCandidateData] =
    useOpenOnNew(setIsCreateModalOpen);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.LEADS_TABLE)
  );

  const { data: entityFieldsResponse } = useEntityFields(EntityType.LEAD);
  const { enabled: isIcpFitEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5676_ICP
  );
  const enablePartnerships = useEnablePartnerships();
  const { isLoading: isAccountSettingsLoading } = useAccountSettings();
  const customFields = useMemo(() => {
    const fields = entityFieldsResponse?.fields || [];
    const withoutIcp = isIcpFitEnabled
      ? fields
      : excludeFieldsByKey(fields, LEAD_ICP_FIT_FIELD_KEYS);
    return enablePartnerships ? withoutIcp : excludePartnerFields(withoutIcp);
  }, [entityFieldsResponse, isIcpFitEnabled, enablePartnerships]);
  const { filterVisibleColumnKeys } = useFieldVisibility(
    EntityType.LEAD
  );
  const activeLeadsColumnKeys = useMemo(
    () => filterVisibleColumnKeys(leadsColumnKeys),
    [filterVisibleColumnKeys]
  );

  // The two ICP fit columns reach the table through the entity-fields registry,
  // which seeds every field unchecked. They are meant to be visible out of the
  // box on Leads, so tick them in the default state the picker and the seeding
  // effect both read from.
  const defaultColumnsState = useMemo(() => {
    const state = getDefaultColumnsStateWithCustomFields(
      activeLeadsColumnKeys,
      customFields,
      getPageNameFromTableId(TABLE_ID),
      t
    );
    return isIcpFitEnabled
      ? withColumnsVisible(state, LEAD_ICP_FIT_FIELD_KEYS)
      : state;
  }, [activeLeadsColumnKeys, customFields, isIcpFitEnabled, t]);

  // Re-seeds whenever columnsState is empty, not only on first load: switching
  // to a view with no saved columns (the system "My leads" view stores only
  // filters and sort) resets columnsState to [], and the registry columns —
  // the ICP pair among them — render only from columnsState.
  // Wait for account settings so defaultColumnsState reflects the resolved
  // enablePartnerships flag before seeding — otherwise partner columns can
  // seed as excluded and never reappear once the flag resolves true. The ICP
  // flag needs no such wait: seeding before it resolves leaves the ICP
  // columns out, and the view-adoption effect below folds them back in.
  useReconciledColumnsState({
    columnsState,
    setColumnsState,
    defaultColumnsState,
    selectedView,
    flagGatedColumnIds: NO_FLAG_GATED_COLUMN_IDS,
    isReady: Boolean(entityFieldsResponse) && !isAccountSettingsLoading
  });

  // A view carries its own saved columns and replaces columnsState wholesale on
  // activation, so the default state above never reaches a page opened on a
  // view — and every view saved while the ICP columns were still seeding
  // unchecked stores them hidden. Nobody chose that, so tick them as the view
  // is adopted. Keyed on the view rather than run every render: re-applying
  // continuously would make the column impossible to uncheck.
  const icpAdoptedViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isIcpFitEnabled) return;
    // Wait for the entity fields, not merely for a non-empty default state:
    // the defaults carry the hardcoded lead columns from the first render, so
    // they are non-empty long before the registry supplies the ICP pair. Claim
    // the view in that window and this view's one pass is spent on a merge that
    // could not see them, and the fields arriving later find the ref already
    // set.
    if (!entityFieldsResponse || defaultColumnsState.length === 0) return;
    const viewKey = selectedView?.id ?? "";
    if (icpAdoptedViewRef.current === viewKey) return;
    icpAdoptedViewRef.current = viewKey;
    // Two different stale shapes, and a view can be in either: one saved before
    // the ICP fields existed has no entry for them at all, and one saved after
    // has them stored hidden. Fold the missing ones in, then tick whatever is
    // there.
    setColumnsState((prev) =>
      withColumnsVisible(
        mergeMissingDefaultColumns(prev, defaultColumnsState),
        LEAD_ICP_FIT_FIELD_KEYS
      )
    );
  }, [
    isIcpFitEnabled,
    defaultColumnsState,
    entityFieldsResponse,
    selectedView?.id
  ]);

  // A callback function to handle column changes
  const handleColumnsChange = useCallback((updatedColumns?: ColumnData[]) => {
    if (updatedColumns) {
      setColumnsState(updatedColumns);
    }
  }, []);

  const {
    entities: leads,
    hasNextPage,
    isLoading,
    isAggregationsLoading,
    isAggregationsError,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
    aggregations,
    total
  } = useLeadsInfinite(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    aggregationsGroupField,
    undefined,
    sortParams.field,
    sortParams.asc,
    false,
    viewReady,
    ComputedFields.MINIMAL
  );
  const resetLeadsInfinite = useResetLeadsInfinite();

  const leadsFilterOptions = useLeadsFilterOptions(leads);
  const leadsGroupByOptions = useLeadsGroupByOptions();

  const { exportTo: exportLeads, isExporting: isExportingLeads } =
    useEntityExport({
      createRoute: "leads/export/create",
      statusRoute: "leads/export/status",
      tableName: "leads",
      filters,
      sorting,
      searchTerm,
      columnsState,
      totalRows: total,
      getFilterOptions: () => leadsFilterOptions
    });

  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(
    null
  );
  const handleExportLeads = useCallback(
    (format: ExportFormat) => {
      keepMenuOpenForExportRef.current = true;
      setExportingFormat(format);
      exportLeads(format);
    },
    [exportLeads]
  );

  useEffect(() => {
    if (wasExportingRef.current && !isExportingLeads) {
      keepMenuOpenForExportRef.current = false;
      setExportingFormat(null);
      setIsActionsMenuOpen(false);
    }
    wasExportingRef.current = isExportingLeads;
  }, [isExportingLeads]);

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  const resetPage = () => resetLeadsInfinite();

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  };

  return (
    <div data-test="leads-page" className="h-full">
      <LeadsBottomKPIBar
        aggregations={aggregations}
        totalLeads={total}
        isLoading={isLoading || isAggregationsLoading}
        isError={isAggregationsError}
        isRowsError={isError}
      />
      <div
        data-test="leads-content"
        className="flex flex-col max-h-[calc(var(--app-content-h)-var(--table-bottom-space))] h-full"
      >
        <div
          data-test="leads-header"
          className="flex justify-between p-4 pb-2 h-14"
        >
          <div data-test="leads-title" className="flex items-center gap-[10px]">
            <EntityLogoIconContainer entityType={EntityType.LEAD} />
            <Text as="div" variant="lgSubtitleH1" className="p-1">
              {t("leads.title")}
            </Text>
          </div>
          <div data-test="leads-actions" className="flex items-center gap-2">
            <DropdownMenu
              // Non-modal so opening the import Dialog from a menu item doesn't
              // leave `pointer-events: none` stuck on <body> (Radix dropdown +
              // dialog interaction), which freezes the page after the modal closes.
              modal={false}
              open={isActionsMenuOpen}
              onOpenChange={handleActionsMenuOpenChange}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  data-test="leads-import-export-button"
                  variant="secondary"
                  size="lg"
                  className="h-8"
                >
                  <IconContainer>
                    <Sort />
                  </IconContainer>
                  <span>{t("leads.importExport")}</span>
                  <IconContainer>
                    <ChevronDown />
                  </IconContainer>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[15rem]">
                <DropdownMenuListItem
                  option="import"
                  data-test="leads-import-menu-item"
                  text={t("leads.importLeads")}
                  customIcon={
                    <IconContainer size="5" color="secondary">
                      <Import />
                    </IconContainer>
                  }
                  onSelect={handleOpenImportModal}
                />
                <Divider className="my-1" />
                <DropdownMenuListItem
                  option="export-csv"
                  data-test="leads-export-menu-item"
                  text={t("leads.exportViewAsCsv")}
                  disabled={isExportingLeads}
                  customIcon={
                    <IconContainer size="5" color="secondary">
                      {exportingFormat === "csv" ? <Loader /> : <Export />}
                    </IconContainer>
                  }
                  onSelect={() => handleExportLeads("csv")}
                />
                <DropdownMenuListItem
                  option="export-xlsx"
                  data-test="leads-export-excel-menu-item"
                  text={t("leads.exportViewAsExcel")}
                  disabled={isExportingLeads}
                  customIcon={
                    <IconContainer size="5" color="secondary">
                      {exportingFormat === "xlsx" ? <Loader /> : <Export />}
                    </IconContainer>
                  }
                  onSelect={() => handleExportLeads("xlsx")}
                />
              </DropdownMenuContent>
            </DropdownMenu>
            <IconButton
              data-test="leads-create-button"
              onClick={handleOpenCreateModal}
              size="lg"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        <DataTableToolbar
          data-test="leads-toolbar"
          viewType={ViewType.LEADS_TABLE}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          filterOptions={leadsFilterOptions}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          defaultColumnsState={defaultColumnsState}
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          sortByOptions={leadsSortByOptions}
          defaultSortByValue={LeadColumn.NAME}
          onSortingChange={onSortingChange}
          tableId={TABLE_ID}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
          onViewActivated={handleViewActivated}
        />
        <ToolbarActions
          data-test="leads-toolbar-actions"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          filterOptions={leadsFilterOptions}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          groupByOptions={leadsGroupByOptions}
          sorting={sorting}
          onSortingChange={onSortingChange}
          sortByOptions={leadsSortByOptions}
          defaultSortByValue={LeadColumn.NAME}
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          defaultColumnsState={defaultColumnsState}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          viewType={ViewType.LEADS_TABLE}
          onColumnsChange={handleColumnsChange}
          tableId={TABLE_ID}
          customFields={customFields}
          className="p-2 pr-4"
        />
        <LeadsTable
          data={leads}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          onLoadMore={handleLoadMore}
          filters={filters}
          setFilters={setFilters}
          filterOptions={leadsFilterOptions}
          sorting={sorting}
          onSortingChange={onSortingChange}
          groupBy={groupBy}
          aggregations={aggregations}
          isAggregationsLoading={isAggregationsLoading}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
          selectedView={selectedView}
          tableId={TABLE_ID}
          viewReady={viewReady}
          hasActiveFilters={!isEqual(filters, EmptyFilters)}
          onClearFilters={() => setFilters(EmptyFilters)}
          onClearSearch={() => setSearchTerm("")}
          isError={isError}
          onRetry={refetch}
        />
      </div>
      <CreateLeadModal
        data-test="leads-create-modal"
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        userData={userData}
        resetPage={resetPage}
        allowDreamer
        candidateData={candidateData}
        onClose={clearCandidateData}
      />
      <ImportLeadsModal
        data-test="leads-import-modal"
        isOpen={isImportModalOpen}
        setIsOpen={setIsImportModalOpen}
        userData={userData}
      />
    </div>
  );
};

export default Leads;
